import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Bug, Users, Sprout, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1603533084057-86bdbd8c175c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const PLANT = "https://images.unsplash.com/photo-1611843467160-25afb8df1074?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxnYXJkZW5pbmclMjBoYW5kcyUyMHBsYW50aW5nJTIwc2FwbGluZ3xlbnwwfHx8fDE3ODQxNzg1NTF8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: CalendarClock, title: "2-Year Milestone Engine", body: "Set Day 0 and we generate the full care calendar — establishment watering, month-4 transitions, and quarterly field visits." },
  { icon: Bug, title: "Visual Pest Alerts", body: "Side-by-side insect vs. leaf-damage comparisons keyed to your exact tree species. Diagnose before it spreads." },
  { icon: Users, title: "Field Crew Dispatch", body: "Milestones auto-generate work tickets on your Landcare provider's job board — checklists and all." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? (user.role === "provider" ? "/provider" : "/dashboard") : "/auth");

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <img src={HERO} alt="lush green tree canopy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-emerald-950/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/40 via-transparent to-emerald-950/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-32 lg:py-44 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 data-testid="hero-headline" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
              Your Plants Never Miss a Milestone.
            </h1>
            <p data-testid="hero-subheadline" className="mt-6 text-lg text-emerald-50/85 max-w-2xl mx-auto">
              Automated care calendars, monthly email reminders, and expert guidance — from Day 0 to graduation day.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Button data-testid="hero-start-btn" onClick={go} className="rounded-full h-12 px-8 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-semibold text-base">
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button data-testid="hero-pricing-btn" onClick={() => navigate("/pricing")} variant="outline" className="rounded-full h-12 px-8 border-white/30 text-white bg-white/5 hover:bg-white/15">
                View pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <f.icon className="w-8 h-8 text-emerald-700" />
              <h3 className="mt-5 font-serif text-2xl text-emerald-950">{f.title}</h3>
              <p className="mt-3 text-stone-600 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center rounded-3xl bg-emerald-950 overflow-hidden">
          <div className="p-10 lg:p-16">
            <Sprout className="w-9 h-9 text-amber-300" />
            <h2 className="mt-6 font-serif text-3xl lg:text-4xl text-white">Five inputs. A two-year plan.</h2>
            <p className="mt-4 text-emerald-50/70">Tree type, planting date, pot size, soil, and target form. That's all we need to build a soil-aware, species-specific care schedule and route the right jobs to the right crews.</p>
            <Button data-testid="cta-start-btn" onClick={go} className="mt-8 rounded-full h-12 px-8 bg-white text-emerald-950 hover:bg-amber-300">Create your first tree profile</Button>
          </div>
          <img src={PLANT} alt="planting" className="h-72 lg:h-full w-full object-cover" />
        </div>
      </section>
    </div>
  );
}
